import { Button } from "@progress/kendo-react-buttons";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { FieldWrapper } from '@progress/kendo-react-form';
import { Loader } from "@progress/kendo-react-indicators";
import { Label } from '@progress/kendo-react-labels';
import { useEffect, useState } from 'react';
import CenterDivPanel from "../../components/CenterDivPanel";
import { fetchApi } from '../../services/api';
import { JsonResponse } from "TypeGen/json-response";
import { ApiCommand } from "TypeGen/api-command";
import { Checkbox, Input } from "@progress/kendo-react-inputs";
import { DateTimePicker } from "@progress/kendo-react-dateinputs";
import dayjs from 'dayjs';
import { useTrack } from ".";
import DrivingBreakHistoryPopup from "./DrivingBreakHistoryPopup";

type Props = {
  tripId: number;
  onClose: () => void;
}

const DrivingBreakPopup = (props: Props) => {
  const track = useTrack();
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resumeDrivingDateTime, setResumeDrivingDateTime] = useState<Date | null>(null);
  const [isOpenEnded, setIsOpenEnded] = useState(false);
  const [comment, setComment] = useState('');
  const [doNotDisturb, setDoNotDisturb] = useState(false);
  const [links, setLinks] = useState<ApiCommand[]>([]);

  useEffect(() => {
    fetchApi(`/api/Track/GetDrivingBreak/${props.tripId}`)
      .then((response: { ResumeDrivingDateTime: string, Comment: string, DoNotDisturb: boolean, Links: ApiCommand[] }) => {
        const responseResumeDrivingDateTime = dayjs.utc(response.ResumeDrivingDateTime);
        const openEnded = responseResumeDrivingDateTime.isValid() && responseResumeDrivingDateTime.year() === 2999;
        setIsOpenEnded(openEnded);
        setResumeDrivingDateTime(responseResumeDrivingDateTime.toDate());
        setComment(response.Comment);
        setDoNotDisturb(response.DoNotDisturb);
        setLinks(response.Links);
      }).catch((e) => {
        // If not problem details
        if (!e?.status) alert(e);
      });
  }, [props.tripId]);

  const canSaveBreak = isOpenEnded || resumeDrivingDateTime !== null;

  const _saveBreak = () => {
    if (!canSaveBreak) {
      alert('Please select a resume driving date & time or mark the break as open ended.');
      return;
    }

    setLoading(true);

    const data = {
      ResumeDrivingDateTime: resumeDrivingDateTime,
      IsOpenEnded: isOpenEnded,
      Comment: comment,
      DoNotDisturb: doNotDisturb
    }

    fetchApi(`/api/Track/EnterDrivingBreak/${props.tripId}`, data, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          track?.refresh();
          props.onClose();
        } else {
          alert(response.ErrorMessage);
        }
      }
      ).catch((e) => {
        // If not problem details
        if (!e?.status) alert(e);
      })
      .finally(() => setLoading(false));
  }

  const _endBreakNow = () => {
    if (!confirm("End the current driving break now?")) return;
    setLoading(true);

    const data = {
      ResumeDrivingDateTime: null,
      Comment: comment,
      DoNotDisturb: doNotDisturb,
    } as { ResumeDrivingDateTime: Date | null; Comment: string; DoNotDisturb: boolean; }

    fetchApi(`/api/Track/EnterDrivingBreak/${props.tripId}`, data, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          track?.refresh();
          props.onClose();
        } else {
          alert(response.ErrorMessage);
        }
      }
      ).catch((e) => {
        // If not problem details
        if (!e?.status) alert(e);
      })
      .finally(() => setLoading(false));
  }

  return <>
    <Dialog
      className="dialog-w800"
      title={`Driving Break/Pause`}
      onClose={props.onClose}
    >
      {loading && <CenterDivPanel>
        <Loader type="converging-spinner" />
      </CenterDivPanel>}

      <div className="k-form k-form-md p-0">

        <FieldWrapper>
          <Label>Resume Driving Date Time</Label>
          <div>
            {isOpenEnded ? (
              <Input value="No resume time planned." disabled />
            ) : (
              <DateTimePicker
                required
                format="MM/dd/yyyy HH:mm"
                formatPlaceholder="formatPattern"
                value={resumeDrivingDateTime}
                placeholder="Select resume time"
                onChange={(e) => setResumeDrivingDateTime(e.value)}
              />
            )}
          </div>
          <div className="mt-2">
            <Checkbox
              checked={isOpenEnded}
              onChange={(e) => {
                const checked = e.value;
                setIsOpenEnded(checked);
                if (!checked) {
                  setResumeDrivingDateTime(dayjs.utc().add(1, 'hour').toDate());
                  return;
                }
              }}
              label="No Resume Time Planned"
            />
            {isOpenEnded && <div className="text-muted">
              Break remains active until manually ended.
            </div>}           
          </div>
        </FieldWrapper>

        <FieldWrapper className="mt-4">
          <Label>Comment</Label>
          <Input value={comment} onChange={(e) => setComment(e.value)} />
        </FieldWrapper>

        <FieldWrapper className="mt-4">
          <Checkbox disabled checked={doNotDisturb} onChange={(e) => setDoNotDisturb(e.value)} label="Do Not Disturb (Can only be set by driver)" />
          {doNotDisturb && <div className="text-danger">This will skip offering them loads until the resume driving date & time.</div>}
        </FieldWrapper>

      </div>

      <DialogActionsBar layout={"end"}>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button onClick={() => setShowHistory(true)}>History</Button>
        {links.find(x => x.Name === "UpdateDrivingBreak") && <Button themeColor="warning" onClick={_endBreakNow}>End Break</Button>}
        <Button themeColor="primary" onClick={_saveBreak}>{links.find(x => x.Name === "UpdateDrivingBreak") ? 'Update Break/Pause' : 'Save Break/Pause'}</Button>
      </DialogActionsBar>
    </Dialog>
    {showHistory && <DrivingBreakHistoryPopup tripId={props.tripId} onClose={() => setShowHistory(false)} />}
  </>

}
export default DrivingBreakPopup;
